import { useState } from "react";
import type { SortValue } from "@/types/video";
import { REGION_OPTIONS, SORT_OPTIONS, YEAR_OPTIONS } from "@/types/video";

interface FilterToolbarProps {
  region: string;
  year: string;
  sort: SortValue;
  onRegionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onSortChange: (value: SortValue) => void;
  onReset: () => void;
}

interface ChipRowProps {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

function ChipRow({ label, options, value, onChange }: ChipRowProps) {
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-4">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-400">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                active
                  ? "bg-accent-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterToolbar({
  region,
  year,
  sort,
  onRegionChange,
  onYearChange,
  onSortChange,
  onReset,
}: FilterToolbarProps) {
  const [expanded, setExpanded] = useState(false);

  const regionOptions = REGION_OPTIONS.map((item) => ({
    label: item,
    value: item,
  }));
  const yearOptions = YEAR_OPTIONS.map((item) => ({
    label: item.label,
    value: item.value,
  }));
  const sortOptions = SORT_OPTIONS.map((item) => ({
    label: item.label,
    value: item.value,
  }));

  const activeCount =
    (region !== "全部" ? 1 : 0) +
    (year !== "all" ? 1 : 0) +
    (sort !== "latest" ? 1 : 0);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-2 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-equalizer-line text-base text-accent-500"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-1.5 text-xs text-background-50">
              {activeCount}
            </span>
          )}
          <i
            className={
              expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
            }
          ></i>
        </button>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-400 transition-colors hover:text-accent-600 cursor-pointer"
        >
          重置
        </button>
      </div>

      <div
        className={`${
          expanded ? "block" : "hidden"
        } divide-y divide-background-100 px-4 pb-2 md:block md:divide-y-0 md:px-5 md:pb-3`}
      >
        <ChipRow
          label="地区"
          options={regionOptions}
          value={region}
          onChange={onRegionChange}
        />
        <ChipRow
          label="年份"
          options={yearOptions}
          value={year}
          onChange={onYearChange}
        />
        <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-4">
          <ChipRow
            label="排序"
            options={sortOptions}
            value={sort}
            onChange={(value) => onSortChange(value as SortValue)}
          />
          <button
            type="button"
            onClick={onReset}
            className="hidden whitespace-nowrap rounded-full border border-background-200 px-3.5 py-1.5 text-sm text-foreground-500 transition-colors hover:border-accent-300 hover:text-accent-600 md:inline-flex cursor-pointer"
          >
            <i className="ri-refresh-line mr-1"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}